import { strFromU8, unzipSync } from "fflate";

export interface PhoneImportRow {
  loginCode: string;
  phone: string;
}

function normalizedHeader(value: string) {
  return value.trim().toLocaleLowerCase("tr-TR").normalize("NFD").replace(/\p{M}/gu, "").replace(/[^a-z0-9]/g, "");
}

function csvRows(source: string) {
  const rows: string[][] = [];
  let row: string[] = [];
  let value = "";
  let quoted = false;
  for (let index = 0; index < source.length; index += 1) {
    const character = source[index]!;
    if (character === '"') {
      if (quoted && source[index + 1] === '"') { value += '"'; index += 1; }
      else quoted = !quoted;
    } else if (character === "," || character === ";" || character === "\t") {
      if (quoted) value += character;
      else { row.push(value); value = ""; }
    } else if ((character === "\n" || character === "\r") && !quoted) {
      if (character === "\r" && source[index + 1] === "\n") index += 1;
      row.push(value); value = "";
      if (row.some((cell) => cell.trim())) rows.push(row);
      row = [];
    } else value += character;
  }
  row.push(value);
  if (row.some((cell) => cell.trim())) rows.push(row);
  return rows;
}

function columnIndex(reference: string) {
  const letters = reference.match(/^[A-Z]+/i)?.[0]?.toUpperCase() ?? "A";
  return [...letters].reduce((value, letter) => value * 26 + letter.charCodeAt(0) - 64, 0) - 1;
}

function xlsxRows(buffer: ArrayBuffer) {
  const files = unzipSync(new Uint8Array(buffer));
  const sharedXml = files["xl/sharedStrings.xml"];
  const shared = sharedXml
    ? [...new DOMParser().parseFromString(strFromU8(sharedXml), "application/xml").getElementsByTagName("si")]
      .map((item) => [...item.getElementsByTagName("t")].map((text) => text.textContent ?? "").join(""))
    : [];
  const sheetPath = Object.keys(files).filter((name) => /^xl\/worksheets\/sheet\d+\.xml$/.test(name)).sort()[0];
  if (!sheetPath) throw new Error("Excel dosyasında çalışma sayfası bulunamadı.");
  const document = new DOMParser().parseFromString(strFromU8(files[sheetPath]!), "application/xml");
  return [...document.getElementsByTagName("row")].map((row) => {
    const values: string[] = [];
    for (const cell of [...row.getElementsByTagName("c")]) {
      const index = columnIndex(cell.getAttribute("r") ?? "A1");
      const type = cell.getAttribute("t");
      const raw = cell.getElementsByTagName("v")[0]?.textContent ?? "";
      values[index] = type === "s"
        ? shared[Number(raw)] ?? ""
        : type === "inlineStr"
          ? [...cell.getElementsByTagName("t")].map((text) => text.textContent ?? "").join("")
          : raw;
    }
    return values;
  }).filter((row) => row.some((cell) => cell?.trim()));
}

function mappedRows(rows: string[][]): PhoneImportRow[] {
  const headers = (rows[0] ?? []).map(normalizedHeader);
  const loginIndex = headers.findIndex((header) => ["sicil", "sicilno", "sicilnumarasi", "personelno", "logincode"].includes(header));
  const phoneIndex = headers.findIndex((header) => ["telefon", "telefonno", "telefonnumarasi", "cep", "ceptelefonu", "phone"].includes(header));
  if (loginIndex < 0 || phoneIndex < 0) throw new Error("Excel başlıklarında Sicil ve Telefon sütunları bulunmalıdır.");
  return rows.slice(1).flatMap((row) => {
    const loginCode = String(row[loginIndex] ?? "").trim();
    const phone = String(row[phoneIndex] ?? "").trim();
    return loginCode && phone ? [{ loginCode, phone }] : [];
  });
}

export async function parsePhoneImportFile(file: File): Promise<PhoneImportRow[]> {
  const extension = file.name.split(".").at(-1)?.toLocaleLowerCase("tr-TR");
  const rows = extension === "xlsx"
    ? xlsxRows(await file.arrayBuffer())
    : csvRows(await file.text());
  const result = mappedRows(rows);
  if (!result.length) throw new Error("Dosyada aktarılabilecek sicil ve telefon satırı bulunamadı.");
  if (result.length > 2000) throw new Error("Tek dosyada en fazla 2000 telefon satırı aktarılabilir.");
  return result;
}
